/**
 * Feature flags.
 *
 * The one place @launchdarkly/react-sdk is imported, the way EsPrime.js is the
 * one place @primereact/ui is. Everything else in the app asks for a flag
 * through the hooks below and never learns which vendor is behind them.
 *
 * ---------------------------------------------------------------------------
 * NAMING -- the prefix says what kind of flag it is, and what its lifetime is
 * ---------------------------------------------------------------------------
 *
 *   rollout*   A feature being turned on gradually. TEMPORARY: once the
 *              feature is fully deployed and available to everyone, both the
 *              flag and every branch behind it get deleted. A rollout flag
 *              that has been on at 100% for a while is technical debt.
 *   switch*    A kill switch. PERMANENT: it exists so something expensive,
 *              risky or third-party can be turned off in a hurry without a
 *              deploy, and it stays for the life of the thing it guards.
 *   variant*   An A/B test. Temporary, and the kind most likely not to be a
 *              boolean -- use useEsString or useEsJson for those.
 *
 * The prefix says LIFETIME, not type. A switch can carry a string:
 * switchEsRibbon is one, and it is a switch because it is a permanent control
 * for something turned on in a hurry and off again, not because of what it
 * holds. This line used to call variant "the only kind that is usually not a
 * boolean", which read as a rule about types and is not one.
 *
 * The rest of the name is camelCase and names the thing, not the change:
 * rolloutEsCodex, not rolloutNewCodexLaunch.
 *
 * ---------------------------------------------------------------------------
 * A FLAG LAUNCHDARKLY CANNOT ANSWER IS OFF
 * ---------------------------------------------------------------------------
 *
 * One rule, stated in useEsBool and nowhere else, since 2026-09-22. There is no
 * per-flag default constant and the hooks take a key alone. Unreachable is a
 * network failure, an outage, an ad blocker, or a checkout with no client ID --
 * and a key that does not exist in the environment reads the same false.
 *
 * The cost is understood: during an outage a flagged feature goes dark for the
 * people a segment had admitted. The other way round is worse -- an outage
 * showing an unreleased feature to everyone, unrecallable without a deploy,
 * because LaunchDarkly being down is the premise.
 *
 * What preceded it argued that a default should reproduce CURRENT behaviour, so
 * something already live when it got a flag defaulted true. That produced one
 * exception, rolloutEsCodex, and several paragraphs defending it -- and it had
 * never once applied, because the codex's targeting did not admit the public
 * either. Do not write it back.
 *
 * Nothing here is the thing that turns every flag on. That is
 * LD_OVERRIDE_DEFAULT below: a deliberate override, and unset in every
 * deployment. esback's EsFlags.java carries the same rule.
 *
 * ---------------------------------------------------------------------------
 * WHO LAUNCHDARKLY SEES, AND WHY SO FEW
 * ---------------------------------------------------------------------------
 *
 * LaunchDarkly bills every distinct context key a month as one client-side MAU,
 * and in September automated runs were ~95% of 8,631. So:
 *
 *   - Every anonymous visitor is ONE context, key 'anonymous' (ANON_CONTEXT).
 *   - A fixture account (aq-*@escapade.gg) is never sent as itself. It is
 *     identified as its tier's shared context, key aqa-<role> (fixtureContextFor),
 *     the same one esback's EsFlags.fixtureContext sends: four contexts a month,
 *     not hundreds. Every rollout flag reads true for it; switches and variants
 *     answer from LaunchDarkly for that tier, as they do for a real account of it.
 *   - A real signed-in account is identified by its id, as before, with live
 *     updates.
 *
 * The client is therefore started only once EsAuth knows who is reading (see
 * EsFlagIdentity), which costs every visitor the /authUser fetch before flags
 * arrive -- the gate already waited on that fetch before it would redirect.
 *
 * ---------------------------------------------------------------------------
 * THE CLIENT-SIDE ID IS PUBLIC
 * ---------------------------------------------------------------------------
 *
 * LD_CLIENT_ID is baked into the main chunk by webpack and is meant to be: it is a
 * client-side ID, readable by anyone with devtools, and it can only read flag
 * values. It is NOT the sdk-* server key, which lives in esback and must never
 * appear anywhere under esfront/. See esfront/webpack.config.js for how the
 * value gets here, and cmd/zdhook-build for where it comes from.
 *
 * ---------------------------------------------------------------------------
 * WHY THE HOOKS ARE CHOSEN AT MODULE LOAD
 * ---------------------------------------------------------------------------
 *
 * With no client ID there is no LaunchDarkly client, and the SDK is blunt
 * about it: createLDReactProvider( '', ... ) throws "You must configure the
 * client with a client-side SDK key", and useLDClient() outside a provider
 * throws while destructuring a null context. Both verified in
 * test/EsFlagsProbe.test.jsx.
 *
 * So an unconfigured build must not mount the provider -- and then it must not
 * call the SDK's hooks either, since they would throw. Choosing per render is
 * not available: a hook cannot be called conditionally.
 *
 * It does not have to be per render. LD_CLIENT_ID is a build-time constant
 * webpack's DefinePlugin substitutes, so esFlagsConfigured is fixed for the
 * whole life of the bundle. Binding each hook once, here at module load, keeps
 * the call order every component makes perfectly stable -- which is all the
 * rules of hooks actually require -- and costs nothing at render.
 *
 * The exported hooks are thin wrappers over those bindings, because of the
 * overrides further down: the wrapper calls the bound hook unconditionally and
 * then decides which answer wins. Same fixed call order, one comparison.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Navigate } from 'react-router-dom';
import {
    basicLogger,
    createLDReactProvider,
    useBoolVariation,
    useInitializationStatus,
    useJsonVariation,
    useLDClient,
    useNumberVariation,
    useStringVariation,
} from '@launchdarkly/react-sdk';

import { VIEW_HOME } from './EsConstants';

// Substituted by webpack.config.js. The name is unqualified here on purpose --
// the secrets Tim set carry the environment in their own names (LD_DEV_CLIENT,
// LD_PROD_CLIENT) and the build collapses whichever one this environment has
// into this one. See webpack.config.js and cmd/zdhook-build.
const LD_CLIENT_ID = process.env.LD_CLIENT_ID || '';

/**
 * Whether this build has a LaunchDarkly client ID at all.
 *
 * False on a checkout that has not run `zdhush pull es local esfront`, and
 * false under jest, which is deliberate: the suite asserts what the app does,
 * not what a flag happens to be set to this afternoon, and no test should open
 * a streaming connection to LaunchDarkly.
 */
export const esFlagsConfigured = Boolean( LD_CLIENT_ID );

// One fixed key, the same one esback's EsFlags.ANONYMOUS uses, so every
// logged-out reader is one MAU. Left keyless, the SDK generated a key per
// browser storage, and AQA clears storage after every scenario, so each one
// minted a new billed context. The price is that a percentage rollout cannot
// split logged-out readers -- all of them land on one side. No escapade flag
// uses one; the rules match es-* segments by role.
//
// role: PUBLIC, matching esback's ANONYMOUS. A logged-out reader IS the public,
// so one clause -- "role is PUBLIC" -- reaches both them and logged-in accounts
// on the floor, which is what anybody writing that rule means. Leaving it off
// made a role rule silently miss every visitor. `anonymous: true` still tells
// the two apart for a rule that needs to: "role is PUBLIC and anonymous is
// false" is logged-in PUBLIC accounts only.
export const ANON_CONTEXT = { kind: 'user', key: 'anonymous', anonymous: true, role: 'PUBLIC' };

// The key is the ACCOUNT ID and the name is the nickname. Neither is the
// identity, and that is the point: the identity is an email address now, and a
// context key is the one field LaunchDarkly keeps -- every context it has
// evaluated sits in its Contexts list until it ages out. Sending addresses there
// would put the whole user base's email in a third party's console.
//
// The id rather than the nickname as the key, because the key is what decides
// evaluation: a percentage rollout hashes it and an individual target lists it.
// A nickname is neither stable nor unique; an id is a person's one permanent
// handle, so they may change their address and their nickname and stay on the
// same side of a rollout.
//
// esback's EsFlags.caller builds this from the same two values, and the two MUST
// agree: a different key for the same person is the same person evaluated as two
// contexts, landing on both sides of a rollout, which looks like an intermittent
// flag rather than a wrong one. That is why /api/v1/authUser carries accountId
// and nickname rather than letting the browser derive either.
//
// No id is the anonymous context rather than a guessed key. A logged-in reader
// whose row has not arrived is not somebody to target.
//
// role is the TIER -- ADMIN, ALPHA, BETA or PUBLIC, an EsAccountRole name -- and
// it is here so a flag can be targeted at a tier instead of at a list of ids
// that goes stale the moment somebody new is invited. esback sets the same
// attribute under the same name in EsFlags.caller; a LaunchDarkly rule matches
// on the attribute NAME, so an attribute only one SDK sets is a rule that fires
// on the server and not in the browser, which reads as a flag that half works.
//
// PUBLIC when the body carried none, which is the SAME fallback esback applies,
// so the two still agree. An absent attribute is not a quiet no -- it matches no
// role clause at all, so a reader this could not classify would drop out of
// every tier rule including the one meant to catch everybody.
// EXPORTED FOR ONE REASON: so the tier can be asserted. A LaunchDarkly rule
// reading "role is one of ALPHA, BETA" works only if this spells the attribute
// the way esback spells it and sends the enum's own name as the value, and both
// are SILENT when wrong -- an attribute nobody sets and an attribute nobody
// matches look identical from the dashboard, because the flag serves its
// default to everyone either way, which is what it does while the rule is off
// as well. Nothing but the test imports these.
export const contextFor = ( pAccount ) =>
    ( pAccount && pAccount.accountId != null )
        ? {
            kind: 'user',
            key: String( pAccount.accountId ),
            name: pAccount.nickname,
            role: pAccount.role ?? 'PUBLIC',
        }
        : ANON_CONTEXT;

// A fixture account's context: one per tier, shared by every fixture of it, the same key and
// role esback's EsFlags.fixtureContext sends. Anonymous, so LaunchDarkly lists none of them.
export const fixtureContextFor = ( pAccount ) => {
    const aRole = pAccount?.role ?? 'PUBLIC';
    return { kind: 'user', key: `aqa-${ aRole }`, anonymous: true, role: aRole };
};

// ---------------------------------------------------------------------------
// A FIXTURE ACCOUNT IS ITS TIER, AND EVERY ROLLOUT FLAG IS ON FOR IT
// ---------------------------------------------------------------------------
//
// The front end's half of EsFlags.isRolloutForFixtureAccount and
// EsFlags.fixtureContext, which must not drift from them. An acceptance run
// driving a DEPLOYED environment cannot rebuild the bundle, so
// LD_OVERRIDE_DEFAULT below cannot reach it; and each fixture's own context key
// was a billed MAU. So a fixture is identified as its tier's shared context
// (fixtureContextFor), and the rollout rule lives here, naming no flag, so a
// flag added tomorrow is on for the suite too.
//
// `rollout` reads true. A switch or a variant reads what LaunchDarkly serves the
// account's tier: a fixture sees what a real account of its role sees
// (zdship 41252684759: switchEsDemoMode, targeted at es-public, hid the Welcome
// demo step from ALPHA fixtures while it read false for them here regardless).
//
// The address decides -- `user` is esback's principal name, the Cognito
// identity -- never the nickname, which anybody may choose. The address is
// RESERVED: esback's EsUsernameValidator refuses it to a registration unless an
// administrator or the service token asks, and a Cognito self sign-up with it
// stays unconfirmed until a link mailed to it is followed. Real accounts are
// untouched: the flags go on governing what actual people see.
const AQA_USER_PREFIX = 'aq-';
const AQA_USER_DOMAIN = '@escapade.gg';
const FLAG_ROLLOUT_PREFIX = 'rollout';

// BOTH halves, matching esback's EsUsernameValidator.isReserved exactly. The
// prefix alone would call a real person whose address happens to begin "aq-" a
// fixture account and turn every rollout flag on for them; the domain alone
// would do it for everybody we host, Tim included.
const isFixtureAccount = ( pUser ) => {
    if ( typeof pUser !== 'string' ) return false;

    const aUser = pUser.toLowerCase();
    return aUser.startsWith( AQA_USER_PREFIX ) && aUser.endsWith( AQA_USER_DOMAIN );
};

// WHO LAUNCHDARKLY CURRENTLY THINKS IS READING, as a store React can watch.
//
// Two facts, and they are kept together because they are written in the same
// place and both have to be observable:
//
//   fixture   this identity is an automated run's account: identified as its
//             tier, and every rollout flag reads true -- see above.
//   applied   the client's context reflects the CURRENT reader. False while
//             EsAuth is still asking esback who that is, and while the
//             identify() carrying the answer is in flight.
//
// ⚠️ THIS WAS A PLAIN `let` UNTIL 2026-09-17, and the comment here claimed the
// write was safe because "identify() makes the client emit -- which re-renders
// every flag consumer". That is true and it is not enough, and the difference
// cost 120 scenarios on every deployed AQA run.
//
// A re-render AFTER the write does not help a component that already decided
// something irreversible BEFORE it. EsRequireFlag redirects, and a redirect is
// irreversible: the LaunchDarkly client answers for the anonymous context in a
// few hundred ms, EsAuth's fetch has not come back yet, so the gate reads a
// flag that is off for anonymous, renders <Navigate replace> and destroys the
// URL. The identify lands a moment later and re-renders a nav bar that is now
// sitting on the home page. That is exactly what the prod captures show:
// es-nav-codex-link, es-nav-characters-link and es-nav-campaigns-link all
// present -- so the forcing DID work -- with the marketing page underneath.
//
// So `applied` exists to be waited for, and the store exists so that waiting is
// a subscription rather than a hope. The object is REPLACED on write rather
// than mutated, because useSyncExternalStore compares snapshots by identity.
//   pins      an AQA run's pinned string and number flags, from /authUser's
//             `flags` (esback's EsFlags.pin). esback sends them for a fixture
//             account alone, so for everybody else this stays empty.
let gIdentity = { fixture: false, applied: false, pins: {} };
const gIdentityWatchers = new Set();

const setIdentity = ( pNext ) => {
    gIdentity = { ...gIdentity, ...pNext };
    gIdentityWatchers.forEach( ( pNotify ) => pNotify() );
};

const subscribeIdentity = ( pNotify ) => {
    gIdentityWatchers.add( pNotify );
    return () => gIdentityWatchers.delete( pNotify );
};

const identitySnapshot = () => gIdentity;

// Bound once at module load, for the reason the header gives for every other
// hook in this file: the choice is a build-time constant, and binding it here
// keeps the call order stable without a conditional call.
//
// It also has to be bound rather than always-subscribed. An unconfigured bundle
// mounts no provider, so EsFlagIdentity is a no-op and this store can never
// change -- subscribing to it would be a hook call on a path that had none, and
// EsFlagsProbe's override tests mount components straight out of a
// resetModules'd import without pinning React precisely because that path was
// hook-free. Reading the object is correct there and costs nothing.
//
// getServerSnapshot as well as getSnapshot: this bundle is not server-rendered
// today, and a hook that throws the moment somebody tries is a trap laid for
// whoever does. The initial value is the honest one -- nobody is identified.
const useIdentity = esFlagsConfigured
    ? () => useSyncExternalStore( subscribeIdentity, identitySnapshot, identitySnapshot )
    : () => gIdentity;

const LDProvider = esFlagsConfigured
    ? createLDReactProvider( LD_CLIENT_ID, ANON_CONTEXT, {
        // Started by EsFlagIdentity once EsAuth has answered, so the first
        // context a fixture account's browser sends is its tier's. See the header.
        deferInitialization: true,
        ldOptions: {
            // Deprecated AND defaulted to true, which is the trap: left alone,
            // the SDK camel-cases every key it receives, so a flag created in
            // LaunchDarkly as "rollout-es-codex" arrives as "rolloutEsCodex"
            // and one created as "rolloutEsCodex" arrives unchanged -- but
            // Code References stops finding either. Off, the key in this file
            // is the key in the dashboard, exactly.
            useCamelCaseFlagKeys: false,
            // The SDK logs every stream open and close at info, in every
            // reader's console. Warnings and errors still show.
            logger: basicLogger( { level: 'warn' } ),
        },
    } )
    : null;

/**
 * Wraps the app so the flag hooks have something to read.
 *
 * Unconfigured, it is a passthrough and no LaunchDarkly client is ever
 * constructed -- see the module comment for why that is decided here rather
 * than per render.
 */
export const EsFlagProvider = esFlagsConfigured
    ? LDProvider
    : ( { children } ) => children;

// Idempotent: the SDK hands back the first start's promise on every later call.
// A failed start is logged and resolved, because the flags then read false,
// which is the documented answer for an unreachable LaunchDarkly.
const start = ( pClient ) =>
    Promise.resolve()
        .then( () => pClient.start() )
        .catch( ( aError ) => console.error( 'LaunchDarkly start failed', aError ) );

/**
 * Starts LaunchDarkly for whoever is reading, once esback has answered -- a
 * fixture account as its tier's shared context (fixtureContextFor).
 *
 * Render it INSIDE both EsFlagProvider and AuthProvider. The client is built
 * on ANON_CONTEXT with initialization deferred, because on first paint nobody
 * knows yet who is reading -- EsAuth is still asking. start() evaluates the
 * anonymous context, which is one shared key; a real account is identified
 * after it.
 *
 * A no-op component when unconfigured, for the same reason as the provider:
 * useLDClient() with no provider above it throws.
 */
export const EsFlagIdentity = esFlagsConfigured
    ? function EsFlagIdentity( { user, account } ) {
        const aClient = useLDClient();
        // Whether the client's context is somebody other than ANON_CONTEXT.
        const aIdentified = useRef( false );

        useEffect( () => {
            // undefined is EsAuth's "we have not asked esback yet", which is
            // not the same as "nobody is logged in" -- see the comment on its
            // loading state.
            if ( user === undefined ) return;

            // A fixture account: identified as its tier's shared context, never
            // as itself. Not applied until the identify lands, like anybody's, so
            // a switch-gated route does not redirect on the anonymous answer.
            if ( isFixtureAccount( user ) ) {
                setIdentity( { fixture: true, applied: false } );
                aIdentified.current = true;
                start( aClient )
                    .then( () => aClient.identify( fixtureContextFor( account ) ) )
                    .catch( ( aError ) => console.error( 'LaunchDarkly identify failed', aError ) )
                    .finally( () => setIdentity( { applied: true } ) );
                return;
            }

            // Nobody logged in, and the client is on ANON_CONTEXT already, or
            // will be once started: nothing to identify.
            //
            // ⚠️ IT STILL COUNTS AS APPLIED, and that line is load-bearing.
            // Holding `applied` back would keep EsRequireFlag's gate shut for
            // the full twenty-second redirect deadline for every logged-out
            // visitor to a switched-off page.
            const aAnonymous = user === null || contextFor( account ) === ANON_CONTEXT;
            if ( aAnonymous && !aIdentified.current ) {
                setIdentity( { fixture: false, applied: true } );
                void start( aClient );
                return;
            }

            // Written BEFORE the identify and separately from `applied`, so a
            // reader whose identify is in flight already reads the right values.
            setIdentity( { fixture: false } );
            aIdentified.current = !aAnonymous;

            start( aClient )
                .then( () => aClient.identify( aAnonymous ? ANON_CONTEXT : contextFor( account ) ) )
                // A failed identify is not worth breaking a page over: the
                // client keeps serving the previous context's values. Logged so
                // it is not silent.
                .catch( ( aError ) => console.error( 'LaunchDarkly identify failed', aError ) )
                // Applied on failure too. A rejected identify is a final answer,
                // and holding the gate open would turn one failed request into a
                // twenty-second stall on a page the reader may be entitled to.
                .finally( () => setIdentity( { applied: true } ) );
        }, [ aClient, user, account ] );

        return null;
    }
    : () => null;


/**
 * How long a gated route waits before rendering rather than holding a blank.
 *
 * <p>Three seconds. Rendering early is the cheap direction -- see
 * {@link EsRequireFlag} for why the two directions are not the same risk.
 */
const FLAGS_SHOW_DEADLINE_MS = 3000;

/**
 * How long it waits before EJECTING somebody from a flag that never answered.
 *
 * <p>Twenty seconds, and the gap between this and the one above is the whole
 * design. A redirect replaces the URL and cannot be taken back, so it is only
 * ever done on a real answer or on a wait long enough that no answer is
 * coming -- and "long enough" had to be revised upward on 2026-09-17, when a
 * prod client took about a MINUTE to initialise. Twenty seconds does not cover
 * that, and is not meant to: past twenty seconds a reader has already decided
 * the page is broken, and landing them somewhere real beats a blank rectangle.
 * What it does cover is every ordinary slow start.
 */
const FLAGS_REDIRECT_DEADLINE_MS = 20000;

/**
 * Whether the client has an answer from LaunchDarkly yet -- <b>or has run out of
 * time asking</b>.
 *
 * <p>Always true when unconfigured: there is nothing to wait for and false is
 * the final answer.
 *
 * <h2>⚠️ The deadline is the whole point, added 2026-09-17</h2>
 *
 * <p>This used to be {@code status !== 'initializing'} and nothing else, and
 * EsRequireFlag renders {@code null} until it goes true. With no bound on the
 * wait, a browser that never reaches LaunchDarkly leaves every gated route --
 * the codex, characters, every campaign page -- <b>permanently blank, with the
 * URL sitting on it</b>. Nothing is logged, nothing errors, and the page simply
 * never arrives.
 *
 * <p>That is not a hypothetical. The common ad blocker lists carry
 * launchdarkly.com, and a blocked request does not fail fast -- it hangs, so
 * the SDK stays 'initializing' rather than reporting the failure this would
 * have coped with. A captive portal, a corporate proxy and LaunchDarkly itself
 * being down all land the same way. Reported in prod on 2026-09-17 as "it does
 * go to the codex, but nothing shows".
 *
 * <p>What the deadline does NOT do is make the flag true. It bounds the WAIT,
 * so a reader on a blocked network gets an answer -- the app as it behaves with
 * no flag service -- rather than a white rectangle with the URL sitting on it.
 * Since 2026-09-22 that answer is false for every flag, so what they get is the
 * unflagged app.
 */
export const useEsFlagsReady = esFlagsConfigured
    ? () => {
        const aStatus = useInitializationStatus().status;
        const aIdentity = useIdentity();
        const aAnswered = aStatus !== 'initializing';

        // ⚠️ THE TWO QUESTIONS ARE NOT THE SAME ONE, since 2026-09-17.
        //
        // "Has LaunchDarkly answered?" and "has it answered about THIS READER?"
        // look alike and differ by the width of a fetch. The client answers for
        // the anonymous context it necessarily starts on within a few hundred
        // ms; EsAuth's call to esback decides who is actually reading and takes
        // longer. Between the two there is a window in which every flag is
        // fully, confidently evaluated -- for the wrong person.
        //
        // Showing tolerated that window. Redirecting cannot: see EsRequireFlag
        // for why a premature <Navigate replace> is the one unrecoverable
        // outcome here. So an eject waits for both, and a paint waits for one.
        const aSettled = aAnswered && aIdentity.applied;

        const [ aShowExpired, setShowExpired ] = useState( false );
        const [ aRedirectExpired, setRedirectExpired ] = useState( false );

        // Two clocks, armed against the two different conditions, because they
        // now wait for different things. Each is armed only while its own
        // condition is unmet and cleared the moment it is met -- so a normal
        // start schedules nothing, and a re-render mid-wait restarts neither.
        useEffect( () => {
            if ( aAnswered ) return undefined;

            const aShow = setTimeout( () => setShowExpired( true ), FLAGS_SHOW_DEADLINE_MS );

            return () => clearTimeout( aShow );
        }, [ aAnswered ] );

        useEffect( () => {
            if ( aSettled ) return undefined;

            const aRedirect = setTimeout( () => setRedirectExpired( true ), FLAGS_REDIRECT_DEADLINE_MS );

            return () => clearTimeout( aRedirect );
        }, [ aSettled ] );

        return {
            answered: aAnswered,
            mayShow: aAnswered || aShowExpired,
            mayRedirect: aSettled || aRedirectExpired,
        };
    }
    : () => ( { answered: true, mayShow: true, mayRedirect: true } );

// ---------------------------------------------------------------------------
// OVERRIDES -- a fixed answer for a flag, baked into the bundle
// ---------------------------------------------------------------------------
//
// The mirror of esback's escapade.launchDarkly.override map and
// escapade.launchDarkly.overrideDefault property, and the two halves are meant
// to be read together: EsFlags.java says the same thing in the same words.
//
//   LD_OVERRIDE           a JSON object of flag key to value. That flag reads
//                         that value, whatever anything else says.
//   LD_OVERRIDE_DEFAULT   'true' or 'false'. What every BOOLEAN flag not named
//                         in LD_OVERRIDE reads.
//
// Both are substituted by webpack.config.js, which validates them, and both are
// EMPTY in every deployment and every image build. Empty changes nothing: the
// hooks answer exactly what they answered before this existed. Under jest they
// are unset too, so the suite is untouched.
//
// WHY A BLANKET DEFAULT AND NOT A LINE PER FLAG. Flags are a deployment
// concern -- a service running in ECS is the only place one decides anything
// for real. A laptop and the acceptance suite exist to exercise the
// application rather than the rollout state, so the bundle they drive is built
// with LD_OVERRIDE_DEFAULT=true and every feature is on. A per-flag list would
// mean the day somebody adds a flag-gated feature they have to remember to add
// a line, and forgetting is silent: the scenarios drive an app that is hiding
// the thing they are about. Nothing to remember is the point.
//
// It applies to BOOLEAN reads only, and there is no ceremony about the rest: a
// variant* flag read with useEsString or useEsJson is not a feature being on or
// off, so it falls through as it always did. A per-key entry beats the default
// and serves every typed hook.
const parseOverride = ( pText ) => {
    if ( !pText ) return {};

    try {
        const aParsed = JSON.parse( pText );
        return ( aParsed && typeof aParsed === 'object' && !Array.isArray( aParsed ) ) ? aParsed : {};
    } catch ( aError ) {
        // Unreachable from a real build -- webpack refuses one it cannot parse
        // -- so this is the jest and hand-edited-bundle path. Loud, and then
        // out of the way.
        console.error( 'LD_OVERRIDE is not a JSON object; ignoring it', aError );
        return {};
    }
};

const FLAG_OVERRIDE = parseOverride( process.env.LD_OVERRIDE );

const FLAG_OVERRIDE_DEFAULT = process.env.LD_OVERRIDE_DEFAULT === 'true' ? true
    : process.env.LD_OVERRIDE_DEFAULT === 'false' ? false
    : undefined;

// hasOwnProperty rather than a plain lookup, because a flag key is a string
// somebody chose and Object.prototype answers to several of them -- pinning
// nothing at all, `useEsBool( 'constructor', false )` would otherwise return a
// function. Undefined means "not pinned".
const pinnedValue = ( pKey ) =>
    Object.prototype.hasOwnProperty.call( FLAG_OVERRIDE, pKey ) ? FLAG_OVERRIDE[ pKey ] : undefined;

// The typed hooks, bound once. Unconfigured, each answers what an unreachable
// LaunchDarkly answers, so the two paths cannot disagree.
const useBoolBase = esFlagsConfigured
    ? useBoolVariation
    : ( pKey, pDefault ) => pDefault;

const useStringBase = esFlagsConfigured
    ? useStringVariation
    : ( pKey, pDefault ) => pDefault;

const useNumberBase = esFlagsConfigured
    ? useNumberVariation
    : ( pKey, pDefault ) => pDefault;

const useJsonBase = esFlagsConfigured
    ? useJsonVariation
    : ( pKey, pDefault ) => pDefault;

// The underlying hook is ALWAYS called and the override is applied to its
// result, rather than the override short-circuiting the call. A hook cannot be
// called conditionally -- that is the same rule that made the bindings above
// happen once at module load -- and "consulted before LaunchDarkly" is about
// which answer wins, not about which lines run.
export const useEsBool = ( pKey ) => {
    const aRead = useBoolBase( pKey, false );
    const aPinned = pinnedValue( pKey );
    // Subscribed rather than read off a module variable, so that a component
    // which rendered before the identity landed re-renders when it does.
    const aFixture = useIdentity().fixture;

    if ( aPinned !== undefined ) return aPinned;
    if ( FLAG_OVERRIDE_DEFAULT !== undefined ) return FLAG_OVERRIDE_DEFAULT;

    // Same position as esback's: after an explicit override, before what
    // LaunchDarkly says. A fixture's other flags are its tier's, below.
    if ( aFixture && pKey.startsWith( FLAG_ROLLOUT_PREFIX ) ) return true;

    return aRead;
};

// A fixture account reads its run's pin, else its tier's answer: see the fixture
// comment above. The bundle's own pin beats the run's, as esback's override beats its pin.
const typedRead = ( pKey, pRead, pIdentity ) => {
    const aPinned = pinnedValue( pKey );
    if ( aPinned !== undefined ) return aPinned;

    if ( Object.hasOwn( pIdentity.pins, pKey ) ) return pIdentity.pins[ pKey ];

    return pRead;
};

export const useEsString = ( pKey, pDefault = '' ) =>
    typedRead( pKey, useStringBase( pKey, pDefault ), useIdentity() );

export const useEsNumber = ( pKey, pDefault ) =>
    typedRead( pKey, useNumberBase( pKey, pDefault ), useIdentity() );

export const useEsJson = ( pKey, pDefault ) =>
    typedRead( pKey, useJsonBase( pKey, pDefault ), useIdentity() );

/**
 * Takes /authUser's `flags`: an AQA run's pins for its fixture account, which
 * EsAuth hands over before it publishes the reader. WHY THIS ROUTE: a string
 * flag has no "on" for LD_OVERRIDE_DEFAULT to give it, LD_OVERRIDE is baked in
 * for a whole run, and the AQA bundle starts no LaunchDarkly client at all -- so
 * a scenario that needs variantEsDualNav scoped can reach the browser only
 * through esback. A module store rather than a hook, so an unconfigured bundle
 * stays hook-free (see useIdentity), and anything rendered after it reads it.
 * Booleans are not taken: the every-feature-on run already answers those.
 */
export const esPinFlags = ( pPins ) => {
    const aPins = {};
    if ( pPins && typeof pPins === 'object' && !Array.isArray( pPins ) ) {
        Object.entries( pPins ).forEach( ( [ aKey, aValue ] ) => {
            if ( typeof aValue === 'string' || typeof aValue === 'number' ) aPins[ aKey ] = aValue;
        } );
    }
    setIdentity( { pins: aPins } );
};

// ---------------------------------------------------------------------------
// The flags themselves
// ---------------------------------------------------------------------------

/**
 * The codex -- the whole of it, reading and admin alike.
 *
 * esback enforces the same flag on the API side (EsFlagFilter), because hiding
 * a menu entry is not access control -- the flag has to hold for someone who
 * types the URL or curls the endpoint.
 */
export const FLAG_ROLLOUT_CODEX = 'rolloutEsCodex';

export const useEsCodexFlag = () => useEsBool( FLAG_ROLLOUT_CODEX );

/**
 * A route that only exists while a flag is on.
 *
 * Off, it sends the reader to the home page rather than rendering anything --
 * so a bookmarked or pasted /view/codex/... URL lands somewhere real instead
 * of on a page frame over an API that is refusing to answer. `replace`, so the
 * back button does not bounce them straight back into the redirect.
 *
 * Deliberately shaped like RequireAuth in EsAuth.js, and it composes with it:
 * a page can be wrapped in both.
 */
export function EsRequireFlag( { flag, children } ) {
    const { mayShow, mayRedirect } = useEsFlagsReady();
    const aOn = useEsBool( flag );

    // Nothing at all until LaunchDarkly has answered, exactly as RequireAuth
    // renders nothing until esback has. "No value yet" and "the value is false"
    // are different states, and treating them as one is what makes a direct
    // link to a switched-off page paint it first: the client mounts its
    // children immediately, so without the wait the page would render, fire its
    // API calls, collect 404s from EsFlagFilter and only then redirect.
    //
    // ⚠️ THE TWO DIRECTIONS ARE NOT THE SAME RISK, and this is why the wait is
    // bounded twice rather than once.
    //
    // Showing a feature early is SELF-CORRECTING: the reader sees a page for a
    // moment and is moved on, which is what happens anyway when a flag is
    // switched off while somebody is looking at it.
    //
    // ⚠️ Since 2026-09-22 an unanswered flag is FALSE rather than its own
    // default, so nothing is shown early any more and only the eject direction
    // is live. The two deadlines stay because they are about the WAIT, not
    // about the value: a reader whose LaunchDarkly is blocked now waits the
    // long deadline and is sent home, where the codex used to appear on the
    // short one. That is the accepted cost of the one rule -- see the top of
    // this file.
    //
    // Redirecting early is NOT. <Navigate replace> destroys the URL, so a
    // reader ejected on a guess cannot get back even when LaunchDarkly answers
    // "on" a moment later -- they are on the home page and the address they
    // followed is gone from their history. That is unrecoverable, and it is
    // exactly what a single short deadline would have caused: prod was observed
    // taking about a minute to initialise on 2026-09-17, which is long enough
    // that a 3s redirect would have bounced people out of pages they were
    // entitled to.
    //
    // So: render on the short deadline, eject only on a real answer or on a
    // long one.
    if ( !aOn && mayRedirect ) return <Navigate to={ VIEW_HOME } replace />;

    if ( !mayShow ) return null;

    // Past the show deadline with no answer, which now reads false. The branch
    // above ejects once the long deadline passes.
    if ( !aOn ) return null;

    return children;
}

/**
 * The codex gate, as a route element wrapper.
 *
 * A named component rather than EsRequireFlag spelled out at each of the
 * fifteen codex routes -- seven public pages and eight admin screens -- so the
 * flag key and its default are stated once and the router stays readable.
 */
export const EsCodexGate = ( { children } ) => (
    <EsRequireFlag flag={ FLAG_ROLLOUT_CODEX }>
        { children }
    </EsRequireFlag>
);

/**
 * Characters -- the sheet, the builder, and the list a player picks from.
 *
 * Two consequences worth knowing before wondering where the section went.
 *
 * A checkout with no LD_CLIENT_ID hides Characters entirely -- and so does a
 * checkout that HAS one, if rolloutEsCharacters does not exist in that
 * LaunchDarkly environment: an unknown key reads false like any other
 * unanswered one. The flag has to be created in the console for any of this to
 * be visible.
 *
 * And under jest esFlagsConfigured is false by design, so every flag reads
 * false and EsCharactersGate redirects. Character tests render the views
 * directly rather than through the gate, and EsFlagsProbe covers the gate.
 *
 * esback enforces the same key in EsFlags.java and EsFlagFilter, because hiding
 * a menu entry is not access control.
 */
export const FLAG_ROLLOUT_CHARACTERS = 'rolloutEsCharacters';

export const useEsCharactersFlag = () => useEsBool( FLAG_ROLLOUT_CHARACTERS );

/**
 * The characters gate, as a route element wrapper.
 *
 * The sibling of EsCodexGate, and used the same way: stated once here so the
 * router names neither the key nor the default at any of the character routes.
 */
export const EsCharactersGate = ( { children } ) => (
    <EsRequireFlag flag={ FLAG_ROLLOUT_CHARACTERS }>
        { children }
    </EsRequireFlag>
);

/**
 * The Console -- campaigns, sessions, and the moment of play.
 *
 * esback enforces the same key in EsFlags.java, and EsFlagFilter refuses every
 * path beginning "campaign" while it is off.
 */
export const FLAG_ROLLOUT_CONSOLE = 'rolloutEsConsole';

/**
 * The dashboard flag, WITH readiness -- unlike the codex and character hooks,
 * which only need the value because their routes are wrapped in a gate that
 * already waits.
 *
 * /view/console is NOT wrapped, and must not be: it exists today, every logged-in
 * reader reaches it from the unconditional Dash nav entry, and a gate would
 * send them to the marketing home page the moment the flag read false -- a
 * regression shipped behind an off flag. So the branch lives inside EsDash,
 * which means EsDash needs the same "no answer yet" state EsRequireFlag has,
 * and reading it meant a way at useEsFlagsReady, which is deliberately
 * module-private. Returning the pair keeps the private hook private and gives
 * the view one call rather than two.
 */
export const useEsDashboardFlag = () => ( {
    // mayShow, which is the SHORT deadline, and not mayRedirect. EsDash's off
    // branch is a rendered message rather than a <Navigate>, so it destroys no
    // URL and corrects itself the moment an answer lands -- the asymmetry
    // EsRequireFlag documents does not apply here. What would apply is leaving
    // a logged-in reader looking at a blank rectangle for twenty seconds, which
    // is the thing this whole deadline exists to stop.
    ready: useEsFlagsReady().mayShow,
    on: useEsBool( FLAG_ROLLOUT_CONSOLE ),
} );

/**
 * The dashboard gate, as a route element wrapper.
 *
 * For the NEW routes only -- /view/campaigns and /view/campaign/:key. They did
 * not exist before the flag, so redirecting a bookmarked URL to the home page
 * is exactly right for them, and exactly wrong for /view/console above.
 */
export const EsDashboardGate = ( { children } ) => (
    <EsRequireFlag flag={ FLAG_ROLLOUT_CONSOLE }>
        { children }
    </EsRequireFlag>
);

/**
 * Flatscreen maps -- the Map pane beside the game console.
 *
 * Off when LaunchDarkly cannot be reached, which is every flag's answer and no
 * longer a fact stated per flag -- hence no default constant here. Off means
 * the console renders exactly as it always has: no edge tab, no pane, and no
 * wrapper around the board either, which is the part worth saying because a
 * wrapper that only ever holds one child still changes a grid.
 *
 * It gates a SCREEN and two endpoints, so there is a server half: mapList and map are refused while it is off, because hiding a tab is
 * not access control. The GM-only upload is the same argument one level down --
 * the button is drawn from the session's tier and the POST is refused on the
 * server, and only the second of those is the rule.
 */
export const FLAG_ROLLOUT_MAPS = 'rolloutEsMaps';

export const useEsMapsFlag = () => useEsBool( FLAG_ROLLOUT_MAPS );

/**
 * Screens at the table -- one bar across the game (Console, Character, Codex, Map,
 * Chat, and the GM's) in place of the Console and Map docks. Off is the docks as
 * they were. No server half yet: the bar only rearranges what the console already
 * reads.
 */
export const FLAG_ROLLOUT_SCREEN_BAR = 'rolloutEsScreenBar';

export const useEsScreenBarFlag = () => useEsBool( FLAG_ROLLOUT_SCREEN_BAR );

/**
 * The Chat -- one chat for the site, in topics by group, and each one's stream.
 *
 * esback refuses every path beginning "chat" while it is off (EsFlagFilter),
 * because hiding the nav entry is not access control.
 */
export const FLAG_ROLLOUT_CHAT = 'rolloutEsChat';

export const useEsChatFlag = () => useEsBool( FLAG_ROLLOUT_CHAT );

/**
 * Looking for Group in the Commons (plans/es-lfg-plan-2026-10-25.md): the Commons' screen bar
 * (Chat, Tables, Table), the posts and applications, and the GM's post form on the campaign
 * page. Off, /view/commons is the Chat page as it was, with no bar. The bar draws under this
 * flag alone; rolloutEsScreenBar is the game's and no prerequisite.
 *
 * esback refuses every path beginning "lfg", and commonsMemory, while it is off
 * (EsFlagFilter), because hiding a screen is not access control.
 */
export const FLAG_ROLLOUT_LFG = 'rolloutEsLfg';

export const useEsLfgFlag = () => useEsBool( FLAG_ROLLOUT_LFG );

export const EsChatGate = ( { children } ) => (
    <EsRequireFlag flag={ FLAG_ROLLOUT_CHAT }>
        { children }
    </EsRequireFlag>
);

/**
 * The announcement ribbon -- a line of text across the top of every page.
 *
 * A STRING flag, so it is worth saying why the name starts `switch` rather than
 * `variant`. The prefixes above say what a flag's LIFETIME is, not what its
 * type is: `variant` means an A/B test, which this is not -- every reader sees
 * the same ribbon. This is a permanent control
 * for something switched on in a hurry and off again a week later, which is
 * what `switch` means. The type is incidental and the lifetime is not.
 *
 * Its value IS the announcement, so there is no second flag saying whether the
 * ribbon is on: empty means off. See EsRibbon.js for why one field beats two.
 *
 * Empty when LaunchDarkly cannot answer, which is the string form of the rule
 * this file states at the top: an unreachable LaunchDarkly takes the ribbon
 * DOWN rather than leaving a stale one up with no way to remove it. Empty for a
 * fixture account too, which never asks.
 *
 * NOT affected by LD_OVERRIDE_DEFAULT. That switch reads "every feature on" and
 * useEsBool honours it; useEsString cannot, because there is no string that
 * means "on" -- so the AQA one-pass run sees no ribbon, which is correct: a
 * ribbon is content somebody wrote, not a feature to be exercised. A scenario
 * that wants one pins a value through FLAG_OVERRIDE, the same as any other
 * flag.
 */
export const FLAG_SWITCH_RIBBON = 'switchEsRibbon';

export const useEsRibbonFlag = () => useEsString( FLAG_SWITCH_RIBBON );

/**
 * Copy from Crafter. A permanent kill switch: on reads Crafter (a page, the nav and
 * footer, a screen's strings) and falls back to the bundled snapshot when Crafter does not
 * answer; off -- and so off when LaunchDarkly is unreachable -- renders the pages exactly as
 * they were written in esfront. esback's half is EsFlags.FLAG_SWITCH_CRAFTER.
 */
export const FLAG_SWITCH_CRAFTER = 'switchEsCrafter';

export const useEsCrafterFlag = () => useEsBool( FLAG_SWITCH_CRAFTER );

/**
 * Copykeys: site copy from esback's database in the reader's locale, the nav's locale menu,
 * and lang/dir on <html>. Off -- and so off when LaunchDarkly is unreachable -- t() answers the
 * literal its call site wrote and nothing asks esback for copy. esback refuses the copy,
 * translate, and translator stems while it is off (EsFlagFilter).
 */
export const FLAG_ROLLOUT_TRANSLATIONS = 'rolloutEsTranslations';

export const useEsTranslationsFlag = () => useEsBool( FLAG_ROLLOUT_TRANSLATIONS );

export const EsCopykeysGate = ( { children } ) => (
    <EsRequireFlag flag={ FLAG_ROLLOUT_TRANSLATIONS }>
        { children }
    </EsRequireFlag>
);

/**
 * Whether alerting is on: the admin Alerts screen and, in esback, every target an event is
 * sent to. A plain server-side switch; who may open the screen stays ROLE_ADMIN's.
 * esback's half is EsFlags.FLAG_SWITCH_ALERTS.
 */
export const FLAG_SWITCH_ALERTS = 'switchEsAlerts';

export const useEsAlertsFlag = () => useEsBool( FLAG_SWITCH_ALERTS );

export const EsAlertsGate = ( { children } ) => (
    <EsRequireFlag flag={ FLAG_SWITCH_ALERTS }>
        { children }
    </EsRequireFlag>
);

/**
 * The two navs in a game, a string naming the design (Tim, 2026-10-20). Both keep the site's
 * nav unfolded in a game on desktop; `scoped` adds the table's name at the head of the screens
 * bar, a link to the campaign page, and the table's name as the nav Console's tip
 * (plans/nav-consolidation-plan-2026-10-20.md, "Outer and inner nav"). Anything but `scoped`
 * reads `classic`: unreachable, unconfigured, and a fixture account -- AQA's every-flag-on run
 * included, which LD_OVERRIDE_DEFAULT does not reach because it blankets booleans only --
 * unless its scenario pins `scoped` for its run (esPinFlags).
 */
export const FLAG_VARIANT_DUAL_NAV = 'variantEsDualNav';

export const DUAL_NAV_SCOPED = 'scoped';

export const useEsDualNavScoped = () => useEsString( FLAG_VARIANT_DUAL_NAV ) === DUAL_NAV_SCOPED;

/**
 * The live concurrent-users count (Tim, 2026-10-27), shown to the alpha group: switchEsCcu
 * says who sees it, and esback refuses the count's socket to anybody it does not serve
 * (EsFlags.FLAG_SWITCH_CCU). variantEsCcuPlace says where: `menu` (the account menu),
 * `nav`, or `footer` (EsCcu.js). Anything else is nowhere -- unreachable, unconfigured, and
 * a fixture account, AQA's every-flag-on run included, since LD_OVERRIDE_DEFAULT blankets
 * booleans only -- unless its scenario pins a place for its run (esPinFlags). That keeps a count
 * that differs run to run out of every snapshot that did not ask for it.
 */
/**
 * The demo table: every signed-in account plays at the beatles campaign, which esback resets
 * to its seed every hour, on the hour. esback's half is EsFlags.FLAG_SWITCH_DEMO_MODE, and it decides;
 * this only offers the way in.
 */
export const FLAG_SWITCH_DEMO_MODE = 'switchEsDemoMode';

export const useEsDemoModeFlag = () => useEsBool( FLAG_SWITCH_DEMO_MODE );

export const FLAG_SWITCH_CCU = 'switchEsCcu';

export const FLAG_VARIANT_CCU_PLACE = 'variantEsCcuPlace';

export const CCU_PLACES = [ 'menu', 'nav', 'footer' ];

/** Where this reader sees the count, or null for nowhere. */
export const useEsCcuPlace = () => {
    const aOn = useEsBool( FLAG_SWITCH_CCU );
    const aPlace = useEsString( FLAG_VARIANT_CCU_PLACE );
    return aOn && CCU_PLACES.includes( aPlace ) ? aPlace : null;
};

/**
 * The Homebrew Engine (es-homebrew-engine.md §6): custom rulesys -- the sheet, the builder,
 * the Roster, and the Actions tabs drawn from the campaign's or character's definition, the
 * Rulesystems group that forks and publishes one -- and what the engine runs of a definition
 * beyond today's tree. Off is today's hard-coded 5e surfaces, and esback refuses the rulesys
 * endpoints. rolloutEsHomebrewEngine was retired into it (2026-10-20); esback's half is
 * EsFlags.isHomebrewEngineOn.
 */
export const FLAG_ROLLOUT_HOMEBREW_ENGINE = 'rolloutEsHomebrewEngine';

export const useEsHomebrewEngineFlag = () => useEsBool( FLAG_ROLLOUT_HOMEBREW_ENGINE );

export const EsRulesysGate = ( { children } ) => (
    <EsRequireFlag flag={ FLAG_ROLLOUT_HOMEBREW_ENGINE }>
        { children }
    </EsRequireFlag>
);

/**
 * Voice at the table (es-voice.md): the campaign page's Voice field, the GM's, and Join voice
 * on the console's bar, which opens the table's Discord channel. esback refuses every path
 * beginning "voice" while it is off (EsFlagFilter); phase 1's link rides on the campaign's own
 * record, so off is this side hiding the field and the button.
 */
export const FLAG_ROLLOUT_VOICE = 'rolloutEsVoice';

export const useEsVoiceFlag = () => useEsBool( FLAG_ROLLOUT_VOICE );

/**
 * Everything drawn over Fullscreen's map: the Roster overlay (EsGame offers it through
 * EsOverlayContext) and the GM's cast button (EsGameMapCast, EsCastReceiver). One flag, so
 * off leaves the console as it was before either: nothing is offered, not hidden after the
 * fact. esback refuses cast/token, the mint, while it is off (EsFlagFilter); a TV already
 * casting goes dark within its token's half hour. esback's half is EsFlags.FLAG_ROLLOUT_OVERLAY.
 */
export const FLAG_ROLLOUT_OVERLAY = 'rolloutEsOverlay';

export const useEsOverlayFlag = () => useEsBool( FLAG_ROLLOUT_OVERLAY );

/**
 * The campaign builder -- an adventure's plan of parts and links, written on the campaign
 * page and published as a pack. esback refuses every path beginning "adventure" while it
 * is off (EsFlagFilter). esback's half is EsFlags.FLAG_ROLLOUT_CAMPAIGN_BUILDER.
 */
export const FLAG_ROLLOUT_CAMPAIGN_BUILDER = 'rolloutEsCampaignBuilder';

export const useEsCampaignBuilderFlag = () => useEsBool( FLAG_ROLLOUT_CAMPAIGN_BUILDER );

export const EsCampaignBuilderGate = ( { children } ) => (
    <EsRequireFlag flag={ FLAG_ROLLOUT_CAMPAIGN_BUILDER }>
        { children }
    </EsRequireFlag>
);

/**
 * The game API engines call (es-unreal-plugin.md): esback refuses /api/game/v1/ while it is
 * off. esfront draws nothing behind it yet; the Devices tab and the link page will.
 * esback's half is EsFlags.FLAG_ROLLOUT_UNREAL_PLUGIN.
 */
export const FLAG_ROLLOUT_UNREAL_PLUGIN = 'rolloutEsUnrealPlugin';

export const useEsUnrealPluginFlag = () => useEsBool( FLAG_ROLLOUT_UNREAL_PLUGIN );

export const EsUnrealPluginGate = ( { children } ) => (
    <EsRequireFlag flag={ FLAG_ROLLOUT_UNREAL_PLUGIN }>
        { children }
    </EsRequireFlag>
);

/**
 * The chat bot: slash commands in a table's chat and the site topics, its lines, the composer's
 * completion, and its settings on the campaign page. Off -- and so off when LaunchDarkly is
 * unreachable -- a typed command is a plain post and the composer offers nothing. esback reads
 * the flag itself on every post (EsBotService), since a command arrives on the chat's routes.
 */
export const FLAG_ROLLOUT_CHAT_BOT = 'rolloutEsChatBot';

export const useEsChatBotFlag = () => useEsBool( FLAG_ROLLOUT_CHAT_BOT );

/**
 * The shared AI service (es-ai-service.md §4): the permanent switch on every model call, the
 * opt-in box on the account page, and Admin → AI. esback refuses every path beginning "ai" while
 * it is off (EsFlagFilter), the kill ladder's first rung. esback's half is EsFlags.isAiOn.
 */
export const FLAG_SWITCH_AI = 'switchEsAi';

export const useEsAiFlag = () => useEsBool( FLAG_SWITCH_AI );

export const EsAiGate = ( { children } ) => (
    <EsRequireFlag flag={ FLAG_SWITCH_AI }>
        { children }
    </EsRequireFlag>
);

/**
 * The three AI layers, each on its own rollout apart from its feature's flag, because reaching
 * beta is a cost event: the builder's helpers, the rules assistant's panel, and the bot's /imp ask,
 * @imp, private topic, and /imp recap ai. Off, each feature is whole without its AI. Each layer
 * also needs switchEsAi, as esback's does, so no AI copy shows while the switch is off.
 */
export const FLAG_ROLLOUT_CAMPAIGN_BUILDER_AI = 'rolloutEsCampaignBuilderAi';

const useEsAiLayer = ( pKey ) => {
    const aSwitch = useEsBool( FLAG_SWITCH_AI );
    const aLayer = useEsBool( pKey );
    return aSwitch && aLayer;
};

export const useEsCampaignBuilderAiFlag = () => useEsAiLayer( FLAG_ROLLOUT_CAMPAIGN_BUILDER_AI );

export const FLAG_ROLLOUT_HOMEBREW_AI = 'rolloutEsHomebrewAi';

export const useEsHomebrewAiFlag = () => useEsAiLayer( FLAG_ROLLOUT_HOMEBREW_AI );

export const FLAG_ROLLOUT_CHAT_BOT_AI = 'rolloutEsChatBotAi';

export const useEsChatBotAiFlag = () => useEsAiLayer( FLAG_ROLLOUT_CHAT_BOT_AI );

/**
 * Content moderation (esback's guardian package): the squelch control a moderator sees and
 * the scan of an author's words; the server reads the same key for the author.
 */
export const FLAG_ROLLOUT_GUARDIAN = 'rolloutEsGuardian';

export const useEsGuardianFlag = () => useEsBool( FLAG_ROLLOUT_GUARDIAN );

export const EsGuardianGate = ( { children } ) => (
    <EsRequireFlag flag={ FLAG_ROLLOUT_GUARDIAN }>
        { children }
    </EsRequireFlag>
);

/**
 * A GM's storyboard scenes (plans/storyboard-scenes-plan-2026-11-02.md): authored for a campaign
 * or a pack, played in sync from the console, and a trailer on an LFG listing. The admin's own
 * trailers on the Storyboard page are not behind it. esback decides it again on every route
 * (EsStoryboardAccess), because hiding a screen is not access control.
 */
export const FLAG_ROLLOUT_SCENES = 'rolloutEsScenes';

export const useEsScenesFlag = () => useEsBool( FLAG_ROLLOUT_SCENES );
